import React, { useState, useRef, useEffect } from 'react';

function _tone(freq: number, vol: number, delayS: number, durS: number) {
  try {
    const ctx  = new (window.AudioContext || (window as any).webkitAudioContext)()
    const osc  = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain); gain.connect(ctx.destination)
    osc.frequency.value = freq; osc.type = 'sine'
    gain.gain.setValueAtTime(vol, ctx.currentTime + delayS)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delayS + durS)
    osc.start(ctx.currentTime + delayS)
    osc.stop(ctx.currentTime + delayS + durS)
    osc.onended = () => ctx.close()
  } catch (_) {}
}
const sigMicStart  = () => { _tone(440, 0.18, 0, 0.08); setTimeout(() => _tone(660, 0.25, 0, 0.1),  100) } // E1a 440→660
const sigMicStop   = () => { _tone(660, 0.25, 0, 0.08); setTimeout(() => _tone(440, 0.18, 0, 0.1),  100) } // E1  660→440
export const sigHold = () => { _tone(880, 0.12, 0, 0.07); setTimeout(() => _tone(880, 0.12, 0, 0.07), 160) } // B1


const appleSpring = 'cubic-bezier(0.25, 1, 0.2, 1)';

interface MlotButtonProps {
  status?: 'default' | 'pushed' | 'working';
  scale?: number;
  onVoiceSearch?: (onDone: () => void) => void;
  onDictationStart?: () => void;
  onDictationEnd?: () => void;
  onWarmup?: () => void;
  onWarmupCancel?: () => void;
  onDebug?: (msg: string) => void;
  className?: string;
}

export const MlotButton: React.FC<MlotButtonProps> = ({
  status: externalStatus,
  scale = 1,
  onVoiceSearch,
  onDictationStart,
  onDictationEnd,
  onWarmup,
  onWarmupCancel,
  onDebug,
  className = ''
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warmupTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTime = useRef<number>(0);
  const isHoldTriggered = useRef<boolean>(false);
  const warmupFired = useRef<boolean>(false);
  const endFired = useRef<boolean>(false);
  // Jeden aktywny wskaźnik na gest. Ujednolicony strumień Pointer Events sam
  // znosi dublowanie touch→mouse (do tego służył `inputTypeRef`), więc ten ref
  // chroni już tylko przed drugim palcem w trakcie trwającego gestu.
  const activePointerId = useRef<number | null>(null);
  const tapBusy = useRef<boolean>(false);
  const tapBusyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Parytet PTT: fizyczny przycisk (gruszka USB / słuchawka BT / symulacja
  // klawiaturowa F1-Spacja) uruchamia dyktowanie z pominięciem lokalnych
  // timerów tego komponentu (onPointerDown/onPointerUp), więc bez tej gałęzi
  // przycisk NIE animowałby się (brak "wciśnięcia"/glow) przy starcie z
  // klawiatury — mimo że nagrywanie faktycznie trwa. `externalStatus`
  // ('working') to jedyny sygnał, że dyktowanie wystartowało niezależnie od
  // gestu dotyku, więc wymuszamy DOKŁADNIE ten sam stan wizualny co dotyk.
  useEffect(() => {
    if (externalStatus === 'default') {
      setIsRecording(false);
      setIsPressed(false);
      // Zewnętrzny reset (np. koniec dyktowania z PTT) zwalnia też blokadę
      // wskaźnika — gdyby jakiś `pointerup`/`pointercancel` nigdy nie dotarł,
      // przycisk i tak wróci do stanu gotowości.
      activePointerId.current = null;
    } else if (externalStatus === 'working') {
      setIsRecording(true);
      setIsPressed(true);
    }
  }, [externalStatus]);

  // ✅ POPRAWKA (Android/PWA): jeden interfejs — Pointer Events — zamiast mieszania
  // `onMouseDown` + `onTouchStart`. NIE wołamy tu `preventDefault()`: reactowe
  // listenery dotyku są na nowoczesnym Chromie PASYWNE, więc `preventDefault()`
  // był no-opem i zaśmiecał konsolę ("Unable to preventDefault inside passive
  // event listener invocation"), a na tablecie z Androidem potrafił zablokować
  // reakcję przycisku. Domyślne gesty przeglądarki (scroll, pinch-zoom,
  // pull-to-refresh) NA SAMYM przycisku ubija CSS `touch-action: none`, a menu
  // kontekstowe długiego przytrzymania — wciąż anulowalny `onContextMenu`.
  const handlePressStart = (e: React.PointerEvent) => {
    // Drugi równoległy wskaźnik (multi-touch) — liczy się tylko pierwszy gest.
    if (activePointerId.current !== null) return;
    activePointerId.current = e.pointerId ?? 0;
    // Przechwycenie wskaźnika: `pointerup` dotrze na przycisk nawet gdy palec
    // / kursor zjedzie poza jego obrys — hold zawsze się domknie. `?.` + try:
    // jsdom i część starszych WebView nie mają tego API / rzucają przy braku
    // aktywnego wskaźnika.
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* brak wsparcia */ }

    startTime.current = Date.now();
    isHoldTriggered.current = false;
    warmupFired.current = false;
    endFired.current = false;
    setIsPressed(true);

    if (warmupTimer.current) clearTimeout(warmupTimer.current);
    if (pressTimer.current) clearTimeout(pressTimer.current);

    // Warmup po 150ms — typowy tap (<150ms) NIE odpali warmup = brak konfliktu mikrofonu
    warmupTimer.current = setTimeout(() => {
      warmupFired.current = true;
      if (onWarmup) onWarmup();
    }, 150);

    // Próg dyktafonu zablokowany na stabilnych 450ms
    pressTimer.current = setTimeout(() => {
      isHoldTriggered.current = true;
      setIsRecording(true);
      onDebug?.('HOLD_OK');
      if (onDictationStart) onDictationStart();
      // E1a: start mikrofonu +200ms
      setTimeout(() => {
        if (navigator.vibrate) navigator.vibrate(80);
        sigMicStart();
      }, 200);
    }, 450);
  };

  // Zwolnienie wskaźnika (mysz / dotyk / pióro). Bez `preventDefault()` — patrz
  // komentarz przy `handlePressStart`. Rozróżnienie tap vs hold i tak zależy
  // wyłącznie od czasu trzymania, nie od systemowego `click`.
  const handlePressEnd = (e: React.PointerEvent) => {
    const pid = e.pointerId ?? 0;
    if (activePointerId.current === null || activePointerId.current !== pid) return;
    activePointerId.current = null;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* brak wsparcia */ }
    if (pressTimer.current) clearTimeout(pressTimer.current);
    if (endFired.current) return;
    endFired.current = true;

    const duration = Date.now() - startTime.current;
    setIsPressed(false);
    if (!externalStatus) setIsRecording(false);

    if (!isHoldTriggered.current && duration < 450) {
      onDebug?.('TAP');
      if (warmupTimer.current) clearTimeout(warmupTimer.current);
      if (warmupFired.current && onWarmupCancel) onWarmupCancel();
      // Blokuj kolejne tappy dopóki ten nie jest skończony
      if (tapBusy.current) return;
      tapBusy.current = true;
      if (tapBusyTimer.current) clearTimeout(tapBusyTimer.current);
      tapBusyTimer.current = setTimeout(() => { tapBusy.current = false; }, 6000); // safety
      // Start nasłuchu: wibracja + systemowy dźwięk SR (nasz wyłączony)
      setTimeout(() => {
        if (navigator.vibrate) navigator.vibrate(80);
        if (onVoiceSearch) onVoiceSearch(() => {
          // E1 stop: dźwięk końca nasłuchu — identyczny jak hold-stop
          if (tapBusyTimer.current) clearTimeout(tapBusyTimer.current);
          tapBusy.current = false;
          setTimeout(() => {
            if (navigator.vibrate) navigator.vibrate(80);
            sigMicStop();
          }, 200);
        });
      }, 200);
    } else {
      onDebug?.(`END hold=${isHoldTriggered.current} dur=${duration}`);
      // E1: koniec mikrofonu +200ms
      setTimeout(() => {
        if (navigator.vibrate) navigator.vibrate(80);
        sigMicStop();
      }, 200);
      if (onDictationEnd) onDictationEnd();
    }
  };

  const handlePressCancel = (e: React.PointerEvent) => {
    const pid = e.pointerId ?? 0;
    if (activePointerId.current !== null && activePointerId.current !== pid) return;
    activePointerId.current = null;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* brak wsparcia */ }
    if (pressTimer.current) clearTimeout(pressTimer.current);
    if (warmupTimer.current) clearTimeout(warmupTimer.current);
    if (isPressed && !endFired.current) {
      endFired.current = true;
      setIsPressed(false);
      if (!externalStatus) setIsRecording(false);
      onDebug?.(`CANCEL hold=${isHoldTriggered.current}`);
      if (isHoldTriggered.current && onDictationEnd) onDictationEnd();
      else if (onWarmupCancel) onWarmupCancel();
    }
  };

  return (
    <div
      onPointerDown={handlePressStart}
      onPointerUp={handlePressEnd}
      onPointerCancel={handlePressCancel}
      // ✅ ABSOLUTNIE KLUCZOWE: Całkowite odcięcie systemowego prawokliku / menu kontekstowego przy trzymaniu palca
      onContextMenu={(e) => e.preventDefault()}
      className={`relative w-[209px] h-[209px] flex items-center justify-center cursor-pointer select-none mx-auto z-40 pointer-events-auto ${className}`}
      style={{
        transition: `transform 200ms ${appleSpring}`,
        transform: isPressed ? `scale(${0.98 * scale})` : `scale(${scale})`,
        // `touch-action: none` — to ono (a nie `preventDefault`) blokuje scroll /
        // pinch-zoom / pull-to-refresh na obszarze przycisku. `user-select` /
        // `-webkit-touch-callout` gaszą zaznaczanie i „lupę" długiego dotyku.
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    >
      <div
        className="absolute inset-0 w-full h-full rounded-full flex items-center justify-center transition-all duration-300"
        style={{
          backdropFilter: 'blur(16.5px)',
          background: isPressed 
            ? 'radial-gradient(50% 50% at 50% 50%, rgba(0, 255, 85, 0.45) 47.12%, rgba(0, 153, 51, 0.15) 100%)'
            : 'radial-gradient(50% 50% at 50% 50%, rgba(0, 255, 85, 0.21) 44.71%, rgba(0, 153, 51, 0.08) 86.54%)',
          boxShadow: isPressed
            ? 'inset 0px 1px 3px rgba(255, 255, 255, 0.6), 0px 0px 25px rgba(0, 255, 85, 0.4)'
            : 'inset 0px 1px 2px rgba(255, 255, 255, 0.4), 0px 8px 32px rgba(0, 0, 0, 0.25)',
          border: isPressed ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(255, 255, 255, 0.08)',
          animation: isRecording ? 'emerald-glow-pulse 1.6s infinite ease-in-out' : 'none'
        }}
      >
        <svg width="209" height="209" viewBox="20 16 209 209" fill="none" xmlns="http://www.w3.org/2000/svg" className="overflow-visible">
          <path d="M94 116C94.7956 116 95.5587 116.316 96.1213 116.879C96.6839 117.441 97 118.204 97 119V125C97 131.365 99.5286 137.47 104.029 141.971C108.53 146.471 114.635 149 121 149C127.365 149 133.47 146.471 137.971 141.971C142.471 137.47 145 131.365 145 125V119C145 118.204 145.316 117.441 145.879 116.879C146.441 116.316 147.204 116 148 116C148.796 116 149.559 116.316 150.121 116.879C150.684 117.441 151 118.204 151 119V125C151 132.437 148.238 139.609 143.249 145.125C138.26 150.64 131.4 154.106 124 154.85V167H142C142.796 167 143.559 167.316 144.121 167.879C144.684 168.441 145 169.204 145 170C145 170.796 144.684 171.559 144.121 172.121C143.559 172.684 142.796 173 142 173H100C99.2044 173 98.4413 172.684 97.8787 172.121C173.3161 171.559 97 170.796 97 170C97 169.204 97.3161 168.441 97.8787 167.879C98.4413 167.316 99.2044 167 100 167H118V154.85C110.6 154.106 103.74 150.64 98.7513 145.125C93.7623 139.609 90.9999 132.437 91 125V119C91 118.204 91.3161 117.441 91.8787 116.879C92.4413 116.316 93.2044 116 94 116Z" fill="url(#paint1_linear_clean_mic)" fillOpacity={isPressed ? 0.75 : 0.35} style={{ transition: 'fill-opacity 150ms ease' }} />
          <path d="M133 125C133 128.183 131.736 131.235 129.485 133.485C127.235 135.736 124.183 137 121 137C117.817 137 114.765 135.736 112.515 133.485C110.264 131.235 109 128.183 109 125V95C109 91.8174 110.264 88.7652 112.515 86.5147C114.765 84.2643 117.817 83 121 83C124.183 83 127.235 84.2643 129.485 86.5147C131.736 88.7652 133 91.8174 133 95V125ZM121 77C116.226 77 111.648 78.8964 108.272 82.2721C104.896 85.6477 103 90.2261 103 95V125C103 129.774 104.896 134.352 108.272 137.728C111.648 141.104 116.226 143 121 143C125.774 143 130.352 141.104 133.728 137.728C137.104 134.352 139 129.774 139 125V95C139 90.2261 137.104 85.6477 133.728 82.2721C130.352 78.8964 125.774 77 121 77Z" fill="url(#paint2_linear_clean_mic)" fillOpacity={isPressed ? 0.95 : 0.65} style={{ transition: 'fill-opacity 150ms ease' }} />
          <defs>
            <linearGradient id="paint1_linear_clean_mic" x1="98.836" y1="116" x2="112.093" y2="179.488" gradientUnits="userSpaceOnUse"><stop stopColor="white"/><stop offset="1" stopColor="#737373" stopOpacity="0.45"/></linearGradient>
            <linearGradient id="paint2_linear_clean_mic" x1="107.702" y1="77" x2="134.298" y2="143" gradientUnits="userSpaceOnUse"><stop stopColor="white"/><stop offset="1" stopColor="#737373" stopOpacity="0.45"/></linearGradient>
          </defs>
        </svg>
      </div>
      <style>{`
        @keyframes emerald-glow-pulse {
          0% { filter: brightness(1) drop-shadow(0 0 12px rgba(0, 255, 85, 0.4)); }
          50% { filter: brightness(1.15) drop-shadow(0 0 30px rgba(0, 255, 85, 0.85)); }
          100% { filter: brightness(1) drop-shadow(0 0 12px rgba(0, 255, 85, 0.4)); }
        }
      `}</style>
    </div>
  );
};